import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import CircularProgress from '@material-ui/core/CircularProgress';

import { ServerImage } from '../../../../server/app';

import InHandStyles from './InHand.styles';

const SCALE = 2;
const HAND_IMAGE_URL = '/images/hand/large.png';

// Types
type Props = {
  device: ServerImage;
};

// Component
const InHand: React.FC<Props> = ({ device }) => {
  // Local state
  const [loading, setLoading] = useState(true);
  const [handImage, setHandImage] = useState<string | undefined>();
  const [deviceImage, setDeviceImage] = useState<string | undefined>();

  // Refs
  const bgWidth = useRef(0);
  const bgHeight = useRef(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const handRef = useRef<HTMLImageElement>(null);
  const deviceRef = useRef<HTMLImageElement>(null);

  // Effects
  useEffect(() => {
    setLoading(true);

    // Load leg image
    const loadHandImage = new Image();

    loadHandImage.src = HAND_IMAGE_URL;
    loadHandImage.onload = () => {
      // Set BG sizes
      bgWidth.current = loadHandImage.naturalWidth / SCALE;
      bgHeight.current = loadHandImage.naturalHeight / SCALE;

      setHandImage(HAND_IMAGE_URL);
    };
  }, []);

  useEffect(() => {
    if (handImage) {
      const container = containerRef.current;
      const hand = handRef.current;

      if (container && hand) {
        // Set sizes to scale
        container.style.width = `${bgWidth.current}px`;
        container.style.height = `${bgHeight.current}px`;
        hand.style.width = `${bgWidth.current}px`;
        hand.style.height = `${bgHeight.current}px`;
      }
    }
  }, [handImage]);

  useEffect(() => {
    if (handImage) {
      // Retrieve the device image
      const retrieveDeviceImage = async () => {
        const image = await axios.get(`/api/image/?path=devices/${device.path}`, {
          responseType: 'blob',
        });

        const imagePath = URL.createObjectURL(image.data);

        // Preload & set
        const tempImage = new Image();

        tempImage.src = imagePath;
        tempImage.onload = () => {
          setDeviceImage(imagePath);
        };
      };

      retrieveDeviceImage();
    }
  }, [device, handImage]);

  useEffect(() => {
    const container = containerRef.current;
    const deviceJS = deviceRef.current;

    if (deviceImage && device.meta.width && device.meta.height && container && deviceJS) {
      // Calculate device offset position
      // - Left: 39%
      // - Bottom: 39%
      const deviceBottom = 0.39 * bgHeight.current;

      // Do we need additional top padding to fit the device?
      const remainingHandHeight = bgHeight.current - deviceBottom;
      const overflowHeight = device.meta.height / SCALE - remainingHandHeight;

      if (overflowHeight > 0) {
        container.style.marginTop = `${overflowHeight}px`;
      }

      // Set device styles
      deviceJS.style.width = `${device.meta.width / SCALE}px`;
      deviceJS.style.height = `${device.meta.height / SCALE}px`;
      deviceJS.style.left = `${0.39 * bgWidth.current}px`;
      deviceJS.style.bottom = `${deviceBottom}px`;

      setLoading(false);
    }
  }, [deviceImage, device.meta.width, device.meta.height]);

  // Render
  return (
    <InHandStyles>
      <div className="loading" hidden={!loading}>
        <CircularProgress />
      </div>

      <div className="container" ref={containerRef} hidden={loading}>
        <img src={handImage} ref={handRef} className="hand" alt="" />
        <img src={deviceImage} ref={deviceRef} className="device" alt="" />
      </div>
    </InHandStyles>
  );
};

export default InHand;
